/* eslint-disable tailwindcss/no-custom-classname */
import {ImgHTMLAttributes, useEffect, useRef} from 'react';
import {twMerge} from '../../../common/twMerge';

interface EqualizerProps extends ImgHTMLAttributes<SVGSVGElement> {
  isPlaying?: boolean;
  // 이퀄라이저 애니메이션 한 싸이클 재생시간
  cycleTime?: number;
  color?: string;
  className?: string;
}

/**
 * 디자인팀에서 이퀄라이저를 제작해주기 어렵다고해서 만든 코드.
 * 지금 건들기는 조금 무리가 있어 보여 우선 v1 그대로 가져왔습니다.
 */
export default function Equalizer({
  isPlaying,
  cycleTime = 1.5,
  color = '#000000',
  className,
  ...props
}: EqualizerProps) {
  const equalizerRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!equalizerRef.current) {
      return;
    }
    function stopAnimation() {
      if (
        equalizerRef.current &&
        !equalizerRef.current.classList.contains('stopAnimation')
      ) {
        equalizerRef.current.classList.add('stopAnimation');
      }
    }
    // 플레이 된 경우 애니메이션 스탑시키는 class 삭제
    if (isPlaying && equalizerRef.current.classList.contains('stopAnimation')) {
      equalizerRef.current.classList.remove('stopAnimation');
      // 플레이 중지 상태인 경우 현재 돌고 있는 애니메이션 사이클이 끝나면 animation을 중지시킨다.
    } else if (!isPlaying) {
      equalizerRef.current.addEventListener(
        'animationiteration',
        stopAnimation
      );
      equalizerRef.current.addEventListener(
        'webkitAnimationIteration',
        stopAnimation
      );
    }

    return () => {
      if (equalizerRef.current) {
        equalizerRef.current.removeEventListener(
          'animationiteration',
          stopAnimation
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
        equalizerRef.current.removeEventListener(
          'webkitAnimationIteration',
          stopAnimation
        );
      }
    };
    // 중간에 다른 props가 변경되어 스타일에 영향을 주면 styled components가 클래스를 초기화하므로 의존성에 넣어준다.
  }, [isPlaying, color, cycleTime, className]);

  return (
    <svg
      ref={equalizerRef}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      version="1.1"
      xmlSpace="preserve"
      width="83"
      height="38"
      viewBox="0 0 83 38"
      className={twMerge('equalizer-container', 'stopAnimation', className)}
      {...props}
    >
      <g>
        <path
          className="st0"
          d="M1.4999990844726554,26.000000915527337 L1.4999990844726554,26.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-11 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v11 C2.9999990844726554,25.33000091552735 2.3299990844726537,26.000000915527337 1.4999990844726554,26.000000915527337 z"
        />
        <path
          d="M1.4999990844726554,26.000000915527337 L1.4999990844726554,26.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-11 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v11 C2.9999990844726554,25.33000091552735 2.3299990844726537,26.000000915527337 1.4999990844726554,26.000000915527337 z"
          className="bar bar1"
        />
        <path
          className="st0"
          d="M9.529999084472657,33.00000091552734 L9.529999084472657,33.00000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-25 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v25 C11.029999084472657,32.33000091552735 10.349999084472657,33.00000091552734 9.529999084472657,33.00000091552734 z"
        />
        <path
          d="M9.529999084472657,33.00000091552734 L9.529999084472657,33.00000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-25 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v25 C11.029999084472657,32.33000091552735 10.349999084472657,33.00000091552734 9.529999084472657,33.00000091552734 z"
          className="bar bar2"
        />
        <path
          className="st0"
          d="M17.499999084472655,29.000000915527337 L17.499999084472655,29.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-17 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v17 C18.999999084472655,28.33000091552735 18.329999084472654,29.000000915527337 17.499999084472655,29.000000915527337 z"
        />
        <path
          d="M17.499999084472655,29.000000915527337 L17.499999084472655,29.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-17 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v17 C18.999999084472655,28.33000091552735 18.329999084472654,29.000000915527337 17.499999084472655,29.000000915527337 z"
          className="bar bar3"
        />
        <path
          className="st0"
          d="M25.499999084472655,38.00000091552734 L25.499999084472655,38.00000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-35 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v35 C26.999999084472655,37.33000091552735 26.329999084472654,38.00000091552734 25.499999084472655,38.00000091552734 z"
        />
        <path
          d="M25.499999084472655,38.00000091552734 L25.499999084472655,38.00000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-35 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v35 C26.999999084472655,37.33000091552735 26.329999084472654,38.00000091552734 25.499999084472655,38.00000091552734 z"
          className="bar bar4"
        />
        <path
          className="st0"
          d="M33.49999908447266,34.50000091552734 L33.49999908447266,34.50000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-28 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v28 C34.99999908447266,33.83000091552735 34.32999908447266,34.50000091552734 33.49999908447266,34.50000091552734 z"
        />
        <path
          d="M33.49999908447266,34.50000091552734 L33.49999908447266,34.50000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-28 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v28 C34.99999908447266,33.83000091552735 34.32999908447266,34.50000091552734 33.49999908447266,34.50000091552734 z"
          className="bar bar5"
        />
        <path
          className="st0"
          d="M41.49999908447266,26.500000915527337 L41.49999908447266,26.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-12 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v12 C42.99999908447266,25.83000091552735 42.32999908447266,26.500000915527337 41.49999908447266,26.500000915527337 z"
        />
        <path
          d="M41.49999908447266,26.500000915527337 L41.49999908447266,26.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-12 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v12 C42.99999908447266,25.83000091552735 42.32999908447266,26.500000915527337 41.49999908447266,26.500000915527337 z"
          className="bar bar6"
        />
        <path
          className="st0"
          d="M49.49999908447266,24.000000915527337 L49.49999908447266,24.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-7 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v7 C50.99999908447266,23.33000091552735 50.32999908447266,24.000000915527337 49.49999908447266,24.000000915527337 z"
        />
        <path
          d="M49.49999908447266,24.000000915527337 L49.49999908447266,24.000000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-7 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v7 C50.99999908447266,23.33000091552735 50.32999908447266,24.000000915527337 49.49999908447266,24.000000915527337 z"
          className="bar bar7"
        />
        <path
          className="st0"
          d="M57.46999908447266,25.500000915527337 L57.46999908447266,25.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-10 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v10 C58.96999908447266,24.83000091552735 58.29999908447266,25.500000915527337 57.46999908447266,25.500000915527337 z"
        />
        <path
          d="M57.46999908447266,25.500000915527337 L57.46999908447266,25.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-10 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v10 C58.96999908447266,24.83000091552735 58.29999908447266,25.500000915527337 57.46999908447266,25.500000915527337 z"
          className="bar bar8"
        />
        <path
          className="st0"
          d="M65.63999908447266,34.50000091552734 L65.63999908447266,34.50000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-28 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v28 C67.13999908447266,33.83000091552735 66.46999908447266,34.50000091552734 65.63999908447266,34.50000091552734 z"
        />
        <path
          d="M65.63999908447266,34.50000091552734 L65.63999908447266,34.50000091552734 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-28 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v28 C67.13999908447266,33.83000091552735 66.46999908447266,34.50000091552734 65.63999908447266,34.50000091552734 z"
          className="bar bar9"
        />
        <path
          className="st0"
          d="M73.53999908447265,30.500000915527337 L73.53999908447265,30.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-20 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v20 C75.03999908447265,29.83000091552735 74.36999908447265,30.500000915527337 73.53999908447265,30.500000915527337 z"
        />
        <path
          d="M73.53999908447265,30.500000915527337 L73.53999908447265,30.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-20 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 l0,0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v20 C75.03999908447265,29.83000091552735 74.36999908447265,30.500000915527337 73.53999908447265,30.500000915527337 z"
          className="bar bar10"
        />
        <path
          className="st0"
          d="M81.47999908447265,26.500000915527337 L81.47999908447265,26.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-12 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v12 C82.97999908447265,25.83000091552735 82.30999908447265,26.500000915527337 81.47999908447265,26.500000915527337 z"
        />
        <path
          d="M81.47999908447265,26.500000915527337 L81.47999908447265,26.500000915527337 c-0.8300000000000001,0 -1.5,-0.6700000000000002 -1.5,-1.5 v-12 c0,-0.8300000000000001 0.6700000000000002,-1.5 1.5,-1.5 h0 c0.8300000000000001,0 1.5,0.6700000000000002 1.5,1.5 v12 C82.97999908447265,25.83000091552735 82.30999908447265,26.500000915527337 81.47999908447265,26.500000915527337 z"
          className="bar bar11"
        />
      </g>
      <style jsx>{`
        .equalizer-container {
          enable-background: new 0 0 210 297;
          animation: equalizerTimer ${cycleTime}s infinite;
        }
        .equalizer-container .st0 {
          opacity: 0.34;
        }
        .equalizer-container .bar {
          transform-origin: center;
          transform: scaleY(0);
        }
        .equalizer-container path {
          fill: ${color};
        }
        .equalizer-container.stopAnimation,
        .equalizer-container.stopAnimation * {
          animation: none !important;
        }
        .equalizer-container .bar1 {
          animation: bar1 ${cycleTime}s infinite;
        }
        .equalizer-container .bar2 {
          animation: bar2 ${cycleTime}s infinite;
        }
        .equalizer-container .bar3 {
          animation: bar3 ${cycleTime}s infinite;
        }
        .equalizer-container .bar4 {
          animation: bar4 ${cycleTime}s infinite;
        }
        .equalizer-container .bar5 {
          animation: bar5 ${cycleTime}s infinite;
        }
        .equalizer-container .bar6 {
          animation: bar6 ${cycleTime}s infinite;
        }
        .equalizer-container .bar7 {
          animation: bar7 ${cycleTime}s infinite;
        }
        .equalizer-container .bar8 {
          animation: bar8 ${cycleTime}s infinite;
        }
        .equalizer-container .bar9 {
          animation: bar9 ${cycleTime}s infinite;
        }
        .equalizer-container .bar10 {
          animation: bar10 ${cycleTime}s infinite;
        }
        .equalizer-container .bar11 {
          animation: bar11 ${cycleTime}s infinite;
        }

        @keyframes equalizerTimer {
        }
        @keyframes bar1 {
          0% {
            transform: scaleY(0);
          }
          16% {
            transform: scaleY(0);
          }
          38% {
            transform: scaleY(1);
          }
          58% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar2 {
          0% {
            transform: scaleY(0);
          }
          26% {
            transform: scaleY(1);
          }
          46% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar3 {
          0% {
            transform: scaleY(0);
          }
          20% {
            transform: scaleY(0);
          }
          42% {
            transform: scaleY(1);
          }
          62% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar4 {
          0% {
            transform: scaleY(0);
          }
          16% {
            transform: scaleY(0);
          }
          38% {
            transform: scaleY(1);
          }
          58% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar5 {
          0% {
            transform: scaleY(0);
          }
          24% {
            transform: scaleY(0);
          }
          46% {
            transform: scaleY(1);
          }
          66% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar6 {
          0% {
            transform: scaleY(0);
          }
          32% {
            transform: scaleY(0);
          }
          54% {
            transform: scaleY(1);
          }
          74% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar7 {
          0% {
            transform: scaleY(0);
          }
          12% {
            transform: scaleY(0);
          }
          34% {
            transform: scaleY(1);
          }
          54% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar8 {
          0% {
            transform: scaleY(0);
          }
          16% {
            transform: scaleY(0);
          }
          38% {
            transform: scaleY(1);
          }
          54% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar9 {
          0% {
            transform: scaleY(0);
          }
          24% {
            transform: scaleY(0);
          }
          46% {
            transform: scaleY(1);
          }
          66% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar10 {
          0% {
            transform: scaleY(0);
          }
          12% {
            transform: scaleY(0);
          }
          34% {
            transform: scaleY(1);
          }
          54% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
        @keyframes bar11 {
          0% {
            transform: scaleY(0);
          }
          20% {
            transform: scaleY(0);
          }
          42% {
            transform: scaleY(1);
          }
          62% {
            transform: scaleY(0);
          }
          100% {
            transform: scaleY(0);
          }
        }
      `}</style>
    </svg>
  );
}
